/**
 * Public product (shop.products) service.
 *
 * Surfaces:
 *   * apps/shop  -- public browse
 *   * apps/sell  -- vendor CRUD
 *   * apps/web /shop module
 *   * apps/admin -- moderation
 *
 * Public read goes through shop.v_product_public (joins to approved
 * vendors only); vendor team CRUD goes through shop.products.
 */

import { supabase } from '../client';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';
import type {
  ShopProduct,
  ShopProductInsert,
  ShopProductUpdate,
  ShopProductType,
  ShopProductForm,
  ShopProductFormSchema,
  ShopInventoryStatus,
  ShopDoseUnit,
  ShopConcentrationUnit,
} from './shopTypes';

export interface ListShopProductsInput extends KeysetInput {
  vendorId?: string;
  productType?: ShopProductType;
  inventoryStatus?: ShopInventoryStatus;
  inventoryCountry?: string;
  inStockOnly?: boolean;
  search?: string;
}

/** Public catalog: only published products from approved vendors. */
export async function listShopProductsPublic(
  input: ListShopProductsInput = {},
): Promise<ListResult<ShopProduct>> {
  const pageSize = clampPageSize(input.pageSize);
  // When a vendorId is supplied we're rendering the vendor's own
  // storefront / profile, which by product policy MUST include
  // discovery-hidden listings (`shop.rpc_admin_hide_product_from_discovery`
  // intentionally only blocks browse/discover, not the vendor's own
  // surface). The `v_product_on_vendor_profile` view exposes the
  // same column set + `discovery_hidden_at` for badge rendering;
  // the discoverable-catalog read still uses `v_product_public`.
  const sourceView = input.vendorId
    ? 'v_product_on_vendor_profile'
    : 'v_product_public';
  let q = (supabase() as any)
    .schema('shop')
    .from(sourceView)
    .select('*', selectCountOption(input.countMode));

  if (input.vendorId) q = q.eq('vendor_id', input.vendorId);
  if (input.productType) q = q.eq('product_type', input.productType);
  if (input.inventoryStatus) q = q.eq('inventory_status', input.inventoryStatus);
  if (input.inventoryCountry) q = q.eq('inventory_country_iso2', input.inventoryCountry);
  if (input.inStockOnly) q = q.in('inventory_status', ['in_stock', 'low_stock']);
  if (input.search && input.search.trim().length > 0) {
    const s = `%${input.search.trim()}%`;
    q = q.or([`name.ilike.${s}`, `slug.ilike.${s}`, `sku.ilike.${s}`].join(','));
  }

  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopProduct[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'updated_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

/** Vendor-team list: all products for a vendor (including unpublished). */
export async function listShopProductsForVendor(
  input: ListShopProductsInput & { vendorId: string; includeArchived?: boolean },
): Promise<ListResult<ShopProduct>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('products')
    .select('*', selectCountOption(input.countMode))
    .eq('vendor_id', input.vendorId);

  if (!input.includeArchived) q = q.is('archived_at', null);
  if (input.productType) q = q.eq('product_type', input.productType);
  if (input.inventoryStatus) q = q.eq('inventory_status', input.inventoryStatus);
  if (input.search && input.search.trim().length > 0) {
    const s = `%${input.search.trim()}%`;
    q = q.or([`name.ilike.${s}`, `slug.ilike.${s}`, `sku.ilike.${s}`].join(','));
  }

  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopProduct[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'updated_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function getShopProductPublic(
  vendorSlug: string,
  productSlug: string,
): Promise<ShopProduct | null> {
  // The view doesn't carry vendor slug; resolve vendor by slug first,
  // then look up the product. Two round-trips but each is single-row.
  const { data: vendor, error: ve } = await (supabase() as any)
    .schema('shop')
    .from('v_vendor_public')
    .select('id')
    .eq('slug', vendorSlug)
    .maybeSingle();
  if (ve) throw ve;
  if (!vendor) return null;
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('v_product_public')
    .select('*')
    .eq('vendor_id', (vendor as { id: string }).id)
    .eq('slug', productSlug)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopProduct | null;
}

export async function getShopProduct(id: string): Promise<ShopProduct | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('products')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopProduct | null;
}

/**
 * Vendor-team lookup of a product by its `(vendor_id, slug)` natural key.
 *
 * Used by sell-app routes that want pretty slug URLs (e.g.
 * `/@<vendor>/products/analytics/<product-slug>`) instead of raw UUIDs.
 * Goes through `shop.products` (not `v_product_public`) so it works for
 * unpublished + archived products too; the vendor's own team is allowed
 * to read its own catalog by RLS regardless of publish state.
 */
export async function getShopProductByVendorAndSlug(
  vendorId: string,
  slug: string,
): Promise<ShopProduct | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('products')
    .select('*')
    .eq('vendor_id', vendorId)
    .eq('slug', slug.toLowerCase())
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopProduct | null;
}

export interface UpsertShopProductInput {
  id?: string;
  vendorId: string;
  slug: string;
  name: string;
  productType?: ShopProductType;
  descriptionMd?: string | null;
  imageUrls?: string[];
  coaUrl?: string | null;
  sku?: string | null;
  purityPct?: number | null;
  dosageForm?: string | null;
  unitSize?: number | null;
  unitSizeUnit?: string | null;
  priceCents?: number;
  currency?: string;
  salePriceCents?: number | null;
  saleStartsAt?: string | null;
  saleEndsAt?: string | null;
  inventoryStatus?: ShopInventoryStatus;
  inventoryCountryIso2?: string | null;
  minOrderQty?: number;
  maxOrderQty?: number | null;
  published?: boolean;
  // ---- typed-form fields (added 2026-06-05) ------------------------
  form?: ShopProductForm | null;
  unitsPerPackage?: number | null;
  dosePerUnit?: number | null;
  dosePerUnitUnit?: ShopDoseUnit | null;
  concentration?: number | null;
  concentrationUnit?: ShopConcentrationUnit | null;
  formAttributes?: Record<string, unknown> | null;
  sourceLocale?: string | null;
}

function toProductPatch(input: UpsertShopProductInput): ShopProductUpdate {
  const patch: ShopProductUpdate = {};
  if (input.slug !== undefined) patch.slug = input.slug.toLowerCase();
  if (input.name !== undefined) patch.name = input.name;
  if (input.productType !== undefined) patch.product_type = input.productType;
  if (input.descriptionMd !== undefined) patch.description_md = input.descriptionMd;
  if (input.imageUrls !== undefined) patch.image_urls = input.imageUrls;
  if (input.coaUrl !== undefined) patch.coa_url = input.coaUrl;
  if (input.sku !== undefined) patch.sku = input.sku;
  if (input.purityPct !== undefined) patch.purity_pct = input.purityPct;
  if (input.dosageForm !== undefined) patch.dosage_form = input.dosageForm;
  if (input.unitSize !== undefined) patch.unit_size = input.unitSize;
  if (input.unitSizeUnit !== undefined) patch.unit_size_unit = input.unitSizeUnit;
  if (input.priceCents !== undefined) patch.price_cents = input.priceCents;
  if (input.currency !== undefined) patch.currency = input.currency;
  if (input.salePriceCents !== undefined) patch.sale_price_cents = input.salePriceCents;
  if (input.saleStartsAt !== undefined) patch.sale_starts_at = input.saleStartsAt;
  if (input.saleEndsAt !== undefined) patch.sale_ends_at = input.saleEndsAt;
  if (input.inventoryStatus !== undefined) patch.inventory_status = input.inventoryStatus;
  if (input.inventoryCountryIso2 !== undefined)
    patch.inventory_country_iso2 = input.inventoryCountryIso2;
  if (input.minOrderQty !== undefined) patch.min_order_qty = input.minOrderQty;
  if (input.maxOrderQty !== undefined) patch.max_order_qty = input.maxOrderQty;
  if (input.published !== undefined) {
    patch.published = input.published;
    if (input.published) patch.published_at = new Date().toISOString();
  }
  if (input.form !== undefined) (patch as Record<string, unknown>).form = input.form;
  if (input.unitsPerPackage !== undefined)
    (patch as Record<string, unknown>).units_per_package = input.unitsPerPackage;
  if (input.dosePerUnit !== undefined)
    (patch as Record<string, unknown>).dose_per_unit = input.dosePerUnit;
  if (input.dosePerUnitUnit !== undefined)
    (patch as Record<string, unknown>).dose_per_unit_unit = input.dosePerUnitUnit;
  if (input.concentration !== undefined)
    (patch as Record<string, unknown>).concentration = input.concentration;
  if (input.concentrationUnit !== undefined)
    (patch as Record<string, unknown>).concentration_unit = input.concentrationUnit;
  if (input.formAttributes !== undefined)
    (patch as Record<string, unknown>).form_attributes = input.formAttributes;
  if (input.sourceLocale !== undefined)
    (patch as Record<string, unknown>).source_locale = input.sourceLocale;
  return patch;
}

export async function createShopProduct(input: UpsertShopProductInput): Promise<ShopProduct> {
  const row: ShopProductInsert = {
    vendor_id: input.vendorId,
    slug: input.slug.toLowerCase(),
    name: input.name,
    ...toProductPatch(input),
  };
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('products')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  return data as ShopProduct;
}

export async function updateShopProduct(
  input: UpsertShopProductInput & { id: string },
): Promise<ShopProduct> {
  const patch = toProductPatch(input);
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('products')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ShopProduct;
}

export async function archiveShopProduct(id: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .from('products')
    .update({
      archived_at: new Date().toISOString(),
      published: false,
    })
    .eq('id', id);
  if (error) throw error;
}

export async function publishShopProduct(id: string, published: boolean): Promise<ShopProduct> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('products')
    .update({
      published,
      published_at: published ? new Date().toISOString() : null,
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ShopProduct;
}

// ============================================================================
// Browse: schema-aware sort + filter + facets (added 2026-06-05)
//
// Backed by:
//   * supabase/migrations/20260605000800_shop_browse_rpcs.sql
//   * shop.rpc_browse_products(filters jsonb, sort text, after jsonb, limit_n int)
//   * shop.rpc_browse_facets(filters jsonb)
// ============================================================================

export type BrowseSort =
  | 'relevance'
  | 'trending_7d'
  | 'trending_24h'
  | 'newest'
  | 'price_asc'
  | 'price_desc'
  | 'rating_desc'
  | 'price_per_mg_asc'
  | 'quality_desc';

export interface BrowseProductsFilters {
  q?: string;
  productType?: ShopProductType[];
  form?: ShopProductForm[];
  vendorId?: string[];
  countryIso2?: string[];
  minPriceCents?: number;
  maxPriceCents?: number;
  minRating?: number;
  inStockOnly?: boolean;
  onSaleOnly?: boolean;
  /** Subset match against `shop.products.form_attributes` (jsonb @>). */
  formAttributeFilters?: Record<string, unknown>;
}

/**
 * Opaque cursor for browse pagination. Built from the last row of the
 * previous page. Each sort mode uses a different `v` shape (number,
 * string, etc.); the cursor is meaningful only for the same sort mode
 * that produced it.
 */
export interface BrowseCursor {
  v: string | number;
  id: string;
}

export interface BrowseProductsResult {
  rows: ShopProduct[];
  nextCursor: BrowseCursor | null;
}

export interface BrowseFacetBucket {
  key: string;
  count: number;
}
export interface BrowseVendorFacet {
  vendor_id: string;
  slug: string;
  company_name: string;
  count: number;
}
export interface BrowseFacets {
  forms: BrowseFacetBucket[];
  product_types: BrowseFacetBucket[];
  vendors: BrowseVendorFacet[];
  price_buckets: BrowseFacetBucket[];
  rating_buckets: BrowseFacetBucket[];
  total: number;
}

function filtersToJsonb(f: BrowseProductsFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.q && f.q.trim()) out.q = f.q.trim();
  if (f.productType && f.productType.length) out.product_type = f.productType;
  if (f.form && f.form.length) out.form = f.form;
  if (f.vendorId && f.vendorId.length) out.vendor_id = f.vendorId;
  if (f.countryIso2 && f.countryIso2.length) out.country_iso2 = f.countryIso2;
  if (typeof f.minPriceCents === 'number') out.min_price_cents = f.minPriceCents;
  if (typeof f.maxPriceCents === 'number') out.max_price_cents = f.maxPriceCents;
  if (typeof f.minRating === 'number') out.min_rating = f.minRating;
  if (f.inStockOnly) out.in_stock_only = true;
  if (f.onSaleOnly) out.on_sale_only = true;
  if (f.formAttributeFilters && Object.keys(f.formAttributeFilters).length > 0) {
    out.form_attribute_filters = f.formAttributeFilters;
  }
  return out;
}

/** Field name on the returned row that the next cursor `v` should be built from, per sort. */
function sortValueField(sort: BrowseSort): keyof ShopProduct {
  switch (sort) {
    case 'price_asc':
    case 'price_desc':
      return 'price_cents'; // RPC coalesces sale_price → price; here we hand back price_cents as the cursor seed
    case 'price_per_mg_asc':
      return 'price_per_mg_cents';
    case 'rating_desc':
      return 'review_avg';
    case 'newest':
      return 'published_at';
    case 'trending_7d':
      return 'trending_score_7d';
    case 'trending_24h':
      return 'trending_score_24h';
    case 'quality_desc':
      return 'listing_quality_score';
    case 'relevance':
    default:
      return 'updated_at';
  }
}

export async function browseProducts(input: {
  filters?: BrowseProductsFilters;
  sort?: BrowseSort;
  after?: BrowseCursor | null;
  limit?: number;
}): Promise<BrowseProductsResult> {
  const filters = input.filters ?? {};
  const sort: BrowseSort = input.sort ?? 'trending_7d';
  const limit = Math.min(Math.max(input.limit ?? 24, 1), 50);

  // IMPORTANT: the singleton supabase client is initialized with
  // `db: { schema: 'public' }`. `rpc_browse_products` lives in the
  // `shop` schema; without the explicit `.schema('shop')` the call is
  // dispatched to `public.rpc_browse_products` which does not exist,
  // surfacing as PGRST202 "Could not find the function" and the page
  // would silently render the empty state with all 325 catalog rows
  // present. Same bug fix below for rpc_browse_facets.
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_browse_products', {
      p_filters: filtersToJsonb(filters),
      p_sort: sort,
      p_after: input.after ?? null,
      p_limit: limit,
    }, { get: false });

  if (error) throw error;
  const rows = (data ?? []) as ShopProduct[];

  let nextCursor: BrowseCursor | null = null;
  if (rows.length >= limit) {
    const last = rows[rows.length - 1];
    if (last) {
      // For price_asc/price_desc the server uses coalesce(sale_price_cents, price_cents).
      // Mirror that here.
      let v: string | number | undefined;
      if (sort === 'price_asc' || sort === 'price_desc') {
        v = last.sale_price_cents ?? last.price_cents;
      } else {
        const field = sortValueField(sort);
        const raw = last[field] as unknown;
        if (typeof raw === 'string' || typeof raw === 'number') v = raw;
      }
      if (v !== undefined && last.id) nextCursor = { v, id: last.id };
    }
  }

  return { rows, nextCursor };
}

export async function getBrowseFacets(
  filters: BrowseProductsFilters = {},
): Promise<BrowseFacets> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_browse_facets', {
      p_filters: filtersToJsonb(filters),
    }, { get: false });
  if (error) throw error;
  return (data ?? {
    forms: [],
    product_types: [],
    vendors: [],
    price_buckets: [],
    rating_buckets: [],
    total: 0,
  }) as BrowseFacets;
}

/** Loads the active form-schema registry. Cached at the consumer layer. */
export async function listProductFormSchemas(): Promise<ShopProductFormSchema[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('product_form_schemas')
    .select('*')
    .eq('active', true)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data ?? []) as ShopProductFormSchema[];
}

// =========================================================================
// Platform-admin moderation
//
// Wraps the four SECURITY DEFINER RPCs introduced in
// `20260601040300_shop_products_moderation.sql`. The RPCs re-check
// `core.is_platform_admin()` server-side so we don't gate here -- but
// we DO surface the friendly error codes (`insufficient_privilege`,
// `invalid_reason`, `product_not_found`) as discriminable JS errors so
// the UI can render the right toast.
//
// Two independent states:
//   - DELISTED: removed from EVERY public surface, vendor cannot edit.
//   - DISCOVERY_HIDDEN: removed from browse/discover only, still on the
//     vendor's own profile, vendor can edit.
//
// Both are reversible by the corresponding `relist` / `unhide` RPC.
// Each call writes one `core.audit_log` row via `core.fn_audit_system`.
// =========================================================================

export class ShopProductModerationError extends Error {
  constructor(
    public readonly reason:
      | 'insufficient_privilege'
      | 'invalid_reason'
      | 'invalid_patch'
      | 'product_not_found'
      | 'slug_conflict'
      | 'unknown',
    message: string,
  ) {
    super(message);
    this.name = 'ShopProductModerationError';
  }
}

function classifyModerationError(err: unknown): ShopProductModerationError {
  const msg = err instanceof Error ? err.message : String(err);
  if (/insufficient_privilege/i.test(msg)) {
    return new ShopProductModerationError('insufficient_privilege', 'Platform admin role required.');
  }
  if (/invalid_reason/i.test(msg)) {
    return new ShopProductModerationError('invalid_reason', 'Reason must be 4..500 characters.');
  }
  if (/invalid_patch/i.test(msg)) {
    // The RPC raises with a HINT like "Unsupported column(s) in
    // patch: foo, bar". Pull whatever the server sent through so the
    // operator sees the specific bad column(s).
    return new ShopProductModerationError(
      'invalid_patch',
      msg.replace(/^.*invalid_patch[:\s]*/i, '').trim() || 'Patch contained unsupported column(s).',
    );
  }
  if (/product_not_found/i.test(msg)) {
    return new ShopProductModerationError('product_not_found', 'Product not found.');
  }
  if (/slug_conflict/i.test(msg)) {
    return new ShopProductModerationError(
      'slug_conflict',
      'Another active product on this vendor already uses this slug. Rename or archive the conflicting product first.',
    );
  }
  return new ShopProductModerationError('unknown', msg);
}

export async function adminDelistShopProduct(input: {
  productId: string;
  reason: string;
}): Promise<ShopProduct> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_delist_product', {
      p_product_id: input.productId,
      p_reason: input.reason,
    });
  if (error) throw classifyModerationError(error);
  return data as ShopProduct;
}

export async function adminRelistShopProduct(input: {
  productId: string;
}): Promise<ShopProduct> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_relist_product', {
      p_product_id: input.productId,
    });
  if (error) throw classifyModerationError(error);
  return data as ShopProduct;
}

export async function adminHideShopProductFromDiscovery(input: {
  productId: string;
  reason: string;
}): Promise<ShopProduct> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_hide_product_from_discovery', {
      p_product_id: input.productId,
      p_reason: input.reason,
    });
  if (error) throw classifyModerationError(error);
  return data as ShopProduct;
}

export async function adminUnhideShopProductFromDiscovery(input: {
  productId: string;
}): Promise<ShopProduct> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_unhide_product_from_discovery', {
      p_product_id: input.productId,
    });
  if (error) throw classifyModerationError(error);
  return data as ShopProduct;
}

/**
 * Platform-admin path to clear `shop.products.archived_at`. Vendor-side
 * `archiveShopProduct` is the only writer that sets the column today,
 * and the Sell app intentionally hides the publish/edit affordances on
 * an archived row -- meaning a vendor who archives by accident has no
 * self-serve way back. This RPC is the operator's restore lever.
 *
 * Idempotent (already-active rows return the row unchanged). Raises
 * `slug_conflict` when restoring would collide with the partial unique
 * `(vendor_id, lower(slug)) where archived_at is null` index.
 */
export async function adminUnarchiveShopProduct(input: {
  productId: string;
}): Promise<ShopProduct> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_unarchive_product', {
      p_product_id: input.productId,
    });
  if (error) throw classifyModerationError(error);
  return data as ShopProduct;
}

/**
 * Platform-admin path to toggle `shop.products.published`. The
 * `published` boolean is normally owned by the vendor through Sell;
 * this RPC exists so an operator can force a listing offline / online
 * without using the more destructive `delist` verb (which also blocks
 * vendor edits) or `archive` (which is vendor-set + soft-deletes the
 * row from every public surface).
 *
 * Idempotent: requesting the current value returns the row unchanged
 * + emits an audit-only `shop.product_publish_state_unchanged` row.
 * Allowed even when `flagged_by_lint = true`; in that case the audit
 * is emitted at severity = 'warn' so misuse is reviewable. The lint
 * trigger only fires on UPDATE OF name, description_md, so flipping
 * `published` here will not re-evaluate banned terms.
 */
export async function adminSetShopProductPublished(input: {
  productId: string;
  published: boolean;
  reason?: string;
}): Promise<ShopProduct> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_set_product_published', {
      p_product_id: input.productId,
      p_published: input.published,
      p_reason: input.reason ?? null,
    });
  if (error) throw classifyModerationError(error);
  return data as ShopProduct;
}

/**
 * Platform-admin false-positive escape hatch for the banned-terms
 * lint. Sets `lint_override_at/by/reason` on the product row and
 * clears `flagged_by_lint` + `flagged_reason` in one transaction.
 * Future vendor edits to name / description_md will not re-flag the
 * row while the override is in place (the trigger short-circuits on
 * `lint_override_at IS NOT NULL`).
 *
 * Does NOT touch `published` -- if the operator also wants the row
 * republished they call `adminSetShopProductPublished` in the same
 * flow. Reason is required (4..500 chars) and lands in the audit log.
 */
export async function adminClearShopProductLint(input: {
  productId: string;
  reason: string;
}): Promise<ShopProduct> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_clear_product_lint', {
      p_product_id: input.productId,
      p_reason: input.reason,
    });
  if (error) throw classifyModerationError(error);
  return data as ShopProduct;
}

/**
 * Revoke a prior lint override. Clears `lint_override_at/by/reason`
 * AND forces the banned-terms trigger to re-evaluate by writing
 * `name` back into itself. If banned terms still match, the trigger
 * restores `flagged_by_lint = true` + `published = false`; otherwise
 * the row stays clean.
 */
export async function adminRestoreShopProductLint(input: {
  productId: string;
}): Promise<ShopProduct> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_restore_product_lint', {
      p_product_id: input.productId,
    });
  if (error) throw classifyModerationError(error);
  return data as ShopProduct;
}

// =========================================================================
// Lint match preview
//
// `shop.products.flagged_reason` only carries the "; "-joined reason
// strings the banned-terms trigger collected. It doesn't tell the
// operator which specific pattern hit, or whether the match landed in
// the product name vs the description. `shop.rpc_admin_preview_product_lint`
// re-runs the same matching loop (`shop.fn_match_banned_terms`) and
// returns one row per matching `shop.banned_terms` entry with
// `(pattern, match_kind, severity, reason, matched_in_name,
// matched_in_description)`.
//
// Returns rows even when the product has a lint override in place —
// the override hides the FLAG but not the underlying matches, and the
// admin UI still wants to render "would have matched these patterns"
// so the operator can decide whether the override should stand.
// =========================================================================

/**
 * One row per matching `shop.banned_terms` entry returned by
 * `shop.rpc_admin_preview_product_lint`. Match semantics mirror the
 * `shop.fn_products_apply_banned_terms` BEFORE trigger:
 * `match_kind='literal'` does `position(lower(pattern) in lower(field)) > 0`,
 * `match_kind='regex'` does `field ~* pattern`.
 */
export interface ShopBannedTermMatch {
  term_id: string;
  pattern: string;
  match_kind: 'literal' | 'regex';
  severity: 'block' | 'warn';
  reason: string | null;
  matched_in_name: boolean;
  matched_in_description: boolean;
}

export async function adminPreviewShopProductLint(input: {
  productId: string;
}): Promise<ShopBannedTermMatch[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_preview_product_lint', {
      p_product_id: input.productId,
    });
  if (error) throw classifyModerationError(error);
  return (data ?? []) as ShopBannedTermMatch[];
}

// =========================================================================
// Admin: list flagged-pending products (dashboard queue)
//
// Powers the new `ShopFlaggedProductsPage` linked from the admin
// operations console "Action required" card. Returns one enriched row
// per product where the banned-terms lint trigger fired AND no admin
// override is in place AND the vendor hasn't archived the row -- i.e.
// every row the operator still needs to triage. Backed by
// `shop.rpc_admin_list_flagged_products(integer)` (see
// `supabase/migrations/20260605150000_admin_work_queue_shop_products_flagged.sql`).
//
// Sort: `flagged_reason` first (so same-cause rows cluster -- the
// "meth substring" Factory Direct block can be dismissed quickly),
// then `vendor_company_name`, then `name`.
// =========================================================================

export interface AdminFlaggedProductRow {
  product_id: string;
  product_name: string;
  product_slug: string;
  product_sku: string | null;
  description_md: string | null;
  flagged_reason: string | null;
  published: boolean;
  delisted: boolean;
  vendor_id: string;
  vendor_slug: string;
  vendor_company_name: string;
  created_at: string;
  updated_at: string;
}

export async function adminListShopFlaggedProducts(
  input: { limit?: number } = {},
): Promise<AdminFlaggedProductRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_list_flagged_products', {
      p_limit: input.limit ?? 100,
    });
  if (error) throw classifyModerationError(error);
  return (data ?? []) as AdminFlaggedProductRow[];
}

// =========================================================================
// Delist-reasons catalog + Lint-verified delist
//
// New as of `20260608010000_shop_product_delist_reasons_and_lint_verified.sql`:
// the admin flagged-products queue now offers a "Lint Verified" action
// that delists a correctly-flagged product with a structured reason from
// `shop.product_delist_reasons` AND clears the lint flag so the row
// leaves the queue in one click.
//
// The buyer-facing PDP renders a "this listing was removed by
// PeptideClients" banner using the public, anon-readable
// `shop.rpc_public_get_delisted_product_brief` RPC -- see
// `apps/shop/src/pages/PublicProductDetailPage.tsx`.
// =========================================================================

/**
 * One row from `shop.rpc_admin_list_delist_reasons()`. The `code` is the
 * stable machine identifier that lands in `shop.products.delisted_reason_code`
 * and `core.audit_log.kind = 'shop.product_lint_confirmed_and_delisted'`;
 * the `label` is what the buyer / vendor see on the PDP banner.
 */
export interface ShopProductDelistReason {
  code: string;
  label: string;
  description: string | null;
  display_order: number;
}

export async function adminListShopProductDelistReasons(): Promise<
  ShopProductDelistReason[]
> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_list_delist_reasons');
  if (error) throw classifyModerationError(error);
  return (data ?? []) as ShopProductDelistReason[];
}

/**
 * Admin "Lint Verified" action from the flagged-products queue. Calls
 * `shop.rpc_admin_lint_verified_delist`, which in a single transaction:
 *
 *   1. delists the product (`delisted_at`, `delisted_by_user_id`,
 *      `delisted_reason_code`, `delisted_reason = <label>[: <notes>]`),
 *   2. clears `flagged_by_lint` + sets the lint override so the row
 *      leaves the queue and future vendor edits will NOT re-flag,
 *   3. audits as `shop.product_lint_confirmed_and_delisted` (severity warn).
 *
 * `reasonCode` MUST come from `adminListShopProductDelistReasons()` --
 * arbitrary strings are rejected server-side with `invalid_reason_code`.
 * `notes` is optional; when supplied it must be 4..500 chars.
 */
export async function adminLintVerifiedDelistShopProduct(input: {
  productId: string;
  reasonCode: string;
  notes?: string | null;
}): Promise<ShopProduct> {
  const trimmedNotes =
    typeof input.notes === 'string' ? input.notes.trim() : '';
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_lint_verified_delist', {
      p_product_id: input.productId,
      p_reason_code: input.reasonCode,
      p_notes: trimmedNotes.length === 0 ? null : trimmedNotes,
    });
  if (error) throw classifyModerationError(error);
  return data as ShopProduct;
}

/**
 * Brief returned by the public/anon
 * `shop.rpc_public_get_delisted_product_brief` RPC. Powers the
 * buyer-PDP "this listing was removed by PeptideClients" banner that
 * replaces the generic "Product not found" 404 for URLs that resolve
 * to a delisted row with a non-null `delisted_reason_code`.
 *
 * `reason_label` is the catalog label (always non-empty); `reason_code`
 * is the stable identifier for analytics / Sentry tagging only -- the
 * UI should NEVER show the code to buyers.
 */
export interface ShopDelistedProductBrief {
  product_name: string;
  vendor_company_name: string;
  vendor_slug: string;
  delisted_at: string;
  reason_code: string;
  reason_label: string;
}

export async function getShopDelistedProductBriefPublic(
  vendorSlug: string,
  productSlug: string,
): Promise<ShopDelistedProductBrief | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_public_get_delisted_product_brief', {
      p_vendor_slug: vendorSlug,
      p_product_slug: productSlug,
    });
  if (error) throw error;
  return (data ?? null) as ShopDelistedProductBrief | null;
}

// =========================================================================
// Admin product edit
//
// Whitelisted patch RPC for admin-on-behalf-of-vendor edits. Wraps
// `shop.rpc_admin_update_product(uuid, jsonb, text)`. Re-fires the
// banned-terms, listing-quality, and translation-queue triggers
// because it runs as a single UPDATE on the underlying row — the same
// trigger side-effects a vendor edit through `apps/sell` would see.
//
// Excludes `published` (use `adminSetShopProductPublished`) and every
// moderation/lint column (each has its own RPC). The patch shape
// mirrors `UpsertShopProductInput` but accepts ONLY the keys the
// vendor editor writes; unsupported keys raise `invalid_patch`.
// =========================================================================

/**
 * Patch shape for `adminUpdateShopProduct`. Mirrors the snake_case
 * column names on `shop.products` (the RPC consumes a jsonb patch and
 * its allow-list is snake_case). Every field is optional; only fields
 * the caller explicitly sets are applied.
 */
export interface AdminUpdateShopProductPatch {
  name?: string;
  slug?: string;
  sku?: string | null;
  description_md?: string | null;
  product_type?: ShopProductType;
  image_urls?: string[];
  coa_url?: string | null;
  purity_pct?: number | null;
  dosage_form?: string | null;
  unit_size?: number | null;
  unit_size_unit?: string | null;
  price_cents?: number;
  currency?: string;
  sale_price_cents?: number | null;
  sale_starts_at?: string | null;
  sale_ends_at?: string | null;
  inventory_status?: ShopInventoryStatus;
  inventory_country_iso2?: string | null;
  min_order_qty?: number;
  max_order_qty?: number | null;
  form?: ShopProductForm | null;
  units_per_package?: number | null;
  dose_per_unit?: number | null;
  dose_per_unit_unit?: ShopDoseUnit | null;
  concentration?: number | null;
  concentration_unit?: ShopConcentrationUnit | null;
  form_attributes?: Record<string, unknown> | null;
  source_locale?: string | null;
}

export async function adminUpdateShopProduct(input: {
  productId: string;
  patch: AdminUpdateShopProductPatch;
  reason?: string;
}): Promise<ShopProduct> {
  // The RPC accepts only the snake_case keys we explicitly send. Drop
  // keys whose values are `undefined` so optional fields don't end up
  // on the wire as nulls (which would CLEAR the column — not the
  // caller's intent).
  const patch: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input.patch)) {
    if (v !== undefined) patch[k] = v;
  }
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_update_product', {
      p_product_id: input.productId,
      p_patch: patch,
      p_reason: input.reason ?? null,
    });
  if (error) throw classifyModerationError(error);
  return data as ShopProduct;
}
